<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { toLogicalEventPoint, toLogicalRect } from '@/utils/screenRotation';

type ProgressBarColor = 'primary' | 'secondary' | 'success' | 'error' | 'warning';
type ProgressBarHeight = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const props = withDefaults(defineProps<{
  color?: ProgressBarColor
  height?: ProgressBarHeight
  interactive?: boolean
}>(), {
  color: 'primary',
  height: 'md',
  interactive: true
});

const emit = defineEmits<{
  (e: 'seek', value: number): void
}>();
const value = defineModel<number>('value', { required: true });

const progressBarRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);

const progressBarHeightClass = computed(() => {
  const heights = {
    xs: 'h-0.5',
    sm: 'h-1',
    md: 'h-1.5',
    lg: 'h-2',
    xl: 'h-2.5'
  };
  return heights[props.height];
});

const progressHandleSizeClasses = computed(() => {
  const sizes = {
    xs: 'w-1 h-1',
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4',
    xl: 'w-5 h-5'
  };
  return sizes[props.height];
});

const colorClasses = {
  primary: 'bg-text-primary',
  secondary: 'bg-secondary-500',
  success: 'bg-success',
  error: 'bg-error',
  warning: 'bg-warning',
};

const progressBarColorClass = computed(() => {
  return colorClasses[props.color] || 'bg-primary';
});

const handleStartDrag = (e: MouseEvent | TouchEvent) => {
  if (!props.interactive) return;
  
  isDragging.value = true;
  updateProgressFromEvent(e);
  
  // Add global event listeners
  // Add window event listeners when starting drag
  if ('touches' in e) {
    window.addEventListener('touchmove', handleMove);
    window.addEventListener('touchend', handleEnd);
  } else {
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
  }
};

const handleMove = (e: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return;
  updateProgressFromEvent(e);
};

const handleEnd = () => {
  if (!isDragging.value) return;
  isDragging.value = false;
  
  // Remove global event listeners
  window.removeEventListener('mousemove', handleMove);
  window.removeEventListener('mouseup', handleEnd);
  window.removeEventListener('touchmove', handleMove);
  window.removeEventListener('touchend', handleEnd);
};

const updateProgressFromEvent = (e: MouseEvent | TouchEvent) => {
  if (!progressBarRef.value) return;

  const point = toLogicalEventPoint(e);
  const rect = toLogicalRect(progressBarRef.value.getBoundingClientRect());
  const axisLength = rect.width || 1;

  const clamped = Math.max(0, Math.min(point.x - rect.left, axisLength));
  const progress = (clamped / axisLength) * 100;

  value.value = progress;
  emit('seek', progress);
};

onMounted(() => {
  // If component is mounted and unmounted quickly, 
  // make sure we clean up event listeners
  if (isDragging.value) {
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
  }
});

onUnmounted(() => {
  window.removeEventListener('mousemove', handleMove);
  window.removeEventListener('mouseup', handleEnd);
  window.removeEventListener('touchmove', handleMove);
  window.removeEventListener('touchend', handleEnd);
});
</script>

<template>
  <div 
    ref="progressBarRef"
    class="w-full flex items-center group relative h-6"
    :class="{ 'cursor-pointer': interactive, 'cursor-default': !interactive }"
    data-chrome-tap-exempt
    @mousedown="handleStartDrag"
    @touchstart.passive="handleStartDrag"
  >
    <!-- Background track -->
    <div :class="['w-full rounded-full overflow-hidden bg-surface', progressBarHeightClass]">
      <!-- Progress fill -->
      <div 
        :class="[progressBarColorClass, 'h-full transition-all duration-0']"
        :style="{ width: `${value}%` }"
      ></div>
    </div>
    
    <!-- Progress handle -->
    <div 
      v-if="interactive"
      class="absolute w-3 h-3 rounded-full -translate-x-1/2 transition-transform duration-200 progress-handle"
      :class="[progressBarColorClass, progressHandleSizeClasses]"
      :style="{ left: `${value}%` }"
    ></div>
  </div>
</template>

<style scoped>
.progress-handle {
  transition: transform 0.2s ease;
}

.progress-handle:hover,
.progress-handle:active {
  transform: scale(1.25);
  filter: brightness(1.2);
}
</style> 
